/* eslint-disable @typescript-eslint/no-throw-literal */
import React, { useEffect, useState } from 'react';
import { useAmp } from 'next/amp';

import Page from 'components/misc/BasePage/Blog';
import * as Sentry from '@sentry/nextjs';
import { DesktopOnly } from 'components/misc/Layout';
import { Head, StickyActionBar } from 'components/blog/article';
import BlogSubHeader from 'components/blog/BlogSubHeader';
import ArticleContent from 'pageContents/BlogContent/ArticleContent';
import blogApi from 'entity/blog/blogApi';
import { addLinksToArticle } from 'entity/blog/blogUtils';

import getShareCount from 'utils/getShareCount';
import { ArticleWithLink } from 'entity/blog/blogTypes';

/**
 * Article page is a hybrid AMP page.
 * We have to ensure that there are no errors in terminal
 * when on article page in AMP mode in development
 * (by adding ?amp=1 to the URL)
 */
const Article = (props: { article: ArticleWithLink }) => {
  const { article } = props;

  if (!article) return null;

  const [relatedArticles, setRelatedArticles] = useState<ArticleWithLink[]>([]);

  // Selectors
  const isAmp = useAmp();

  // get related Articles
  useEffect(() => {
    const fetchRelatedArticles = async () => {
      const articleResponse = await blogApi.getRelatedArticles(article.slug);
      if (articleResponse.ok) {
        setRelatedArticles(articleResponse?.data.posts.map(addLinksToArticle));
      }
    };
    fetchRelatedArticles();
  }, [article]);

  const shareCount = getShareCount(article.engagement);

  return (
    <Page renderSubHeader={() => <BlogSubHeader />}>
      <Head article={article} />
      {!isAmp && (
        <DesktopOnly>
          <StickyActionBar
            slug={article.slug}
            path={`/${article.slug}`}
            shareCount={shareCount}
          />
        </DesktopOnly>
      )}
      <ArticleContent relatedArticles={relatedArticles} article={article} />
    </Page>
  );
};

export async function getStaticPaths() {
  const articles = await blogApi.getArticles({
    page: 1,
    orderBy: 'publishDate',
    perPage: 25,
  });

  const params = articles.data.posts.map(article => {
    return { params: { articleSlug: article.slug } };
  });

  return {
    paths: params,
    fallback: true,
  };
}

export async function getStaticProps(context) {
  const { params } = context;
  const articleResponse = await blogApi.getArticleFromWp(params.articleSlug);

  if (!articleResponse.ok) {
    // Only log non-404 errors to Sentry (404 is normal behavior)
    if (articleResponse.status !== 404) {
      Sentry.captureMessage(
        JSON.stringify(articleResponse.originalError?.message),
      );
    }

    // if a article is 404 from WP, revalidate 10 second later
    if (articleResponse.status === 404) {
      return {
        notFound: true,
        revalidate: 10,
      };
    }

    throw new Error(
      `Failed to fetch article, received status ${articleResponse.status}`,
    );
  }

  const articleWithLink = addLinksToArticle(articleResponse.data);

  return {
    props: {
      article: articleWithLink,
    },
    revalidate: 300,
  };
}

export const config = { amp: 'hybrid' };
export default Article;
